import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, SecondaryButton, PrimaryButton, DangerButton, Toggle } from '../../design-system';
import { Input } from '../../design-system/components/inputs';
import { typography, surfaces, borders, textColors } from '../../design-system/tokens';
import { apiFetch } from '../../core/utils/api';
import { getDisplayDate } from '../../core/utils/dateUtils';
import { APP_DOMAIN } from '../../core/utils/config';

/**
 * WebsiteSettingsModal Component
 * 
 * Modal for editing website container settings including name, domain, and public visibility.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.container - The container object being edited
 * @param {Array} props.versions - Array of versions for this container
 * @param {Function} props.onSave - Callback when settings are saved successfully (receives updated container)
 * @param {string} props.username - Current user's username
 * @param {string} props.timezone - User's timezone preference
 */
const WebsiteSettingsModal = ({
  isOpen,
  onClose,
  container,
  versions = [],
  onSave,
  onRequestDelete,
  username,
  timezone,
}) => {
  const [name, setName] = useState(container?.name || '');
  const [domain, setDomain] = useState(container?.domain || '');
  const [isPublic, setIsPublic] = useState(Boolean(container?.public));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when container changes
  React.useEffect(() => {
    if (container) {
      setName(container.name || '');
      setDomain(container.domain || '');
      setIsPublic(Boolean(container.public));
      setErrors({});
    }
  }, [container]);

  const handleClose = () => {
    if (!saving) {
      setErrors({});
      onClose();
    }
  };

  const handleSave = async () => {
    if (!container?.id) return;
    
    setSaving(true);
    setErrors({});

    try {
      const response = await apiFetch(`/api/containers/${container.id}/update/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          domain: domain.trim(),
          public: isPublic,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data && data.details) {
          setErrors(data.details);
        } else if (data && data.error) {
          setErrors({ general: [data.error] });
        } else {
          setErrors({ general: ['Failed to update website settings'] });
        }
        setSaving(false);
        return;
      }

      // Success - notify parent and close
      onSave(data);
      setSaving(false);
      onClose();
    } catch (e) {
      setErrors({ general: ['Network error while updating settings'] });
      setSaving(false);
    }
  };

  // Validate subdomain
  const validateSubdomain = () => {
    const val = (domain || '').trim();
    const subdomainPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
    
    if (val === '' && !errors.domain) {
      return 'Subdomain is required.';
    }
    
    const invalid = !subdomainPattern.test(val);
    if (invalid && !errors.domain) {
      return 'Subdomain can contain only lowercase letters, numbers, and hyphens, and cannot start or end with a hyphen.';
    }
    
    return null;
  };

  const subdomainError = validateSubdomain();
  const isSaveDisabled = Boolean(subdomainError) || domain.trim() === '';

  // Find published version
  const publishedVersion = versions.find(v => v.is_published);
  
  // Calculate last update time
  let lastUpdated = container?.latest_last_edited_at || null;
  if (!lastUpdated && versions.length > 0) {
    const timestamps = versions.map(v => 
      new Date(v.last_edited_at || v.updated_at || v.created_at || 0).getTime()
    );
    const maxTimestamp = Math.max(...timestamps);
    if (maxTimestamp > 0) {
      lastUpdated = new Date(maxTimestamp).toISOString();
    }
  }
  const hasValidLastUpdate = lastUpdated && !isNaN(new Date(lastUpdated).getTime());

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Website settings"
      size="lg"
      closeOnOverlayClick={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <SecondaryButton onClick={handleClose} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleSave}
            isLoading={saving}
            disabled={isSaveDisabled}
          >
            Save
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        {/* General Error */}
        {errors.general && (
          <div className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
            {errors.general[0]}
          </div>
        )}

        {/* Website Name */}
        <div>
          <label className={`block ${typography.label} ${textColors.secondary} mb-1`}>
            Website name
          </label>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name ? errors.name[0] : ''}
            disabled={saving}
          />
        </div>

        {/* Subdomain */}
        <div>
          <label className={`block ${typography.label} ${textColors.secondary} mb-1`}>
            Subdomain
          </label>
          <div className="flex items-center">
            <span className={`px-3 py-2 ${surfaces.tertiary} border border-r-0 ${borders.default} rounded-l-md ${typography.body} ${textColors.secondary}`}>
              {`${APP_DOMAIN}/${username}/`}
            </span>
            <Input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value.toLowerCase())}
              error={errors.domain ? errors.domain[0] : ''}
              placeholder="your-subdomain"
              className="rounded-l-none"
              disabled={saving}
            />
          </div>
          {subdomainError && (
            <p className={`${typography.caption} text-red-600 dark:text-red-400 mt-1`}>
              {subdomainError}
            </p>
          )}
        </div>

        {/* Public Toggle */}
        <div className={`flex items-start justify-between gap-4 p-3 border ${borders.default} rounded-md ${surfaces.secondary}`}>
          <div className="min-w-0 flex-1">
            <div className={`${typography.label} ${textColors.primary}`}>Public</div>
            <div className={`${typography.caption} ${textColors.tertiary}`}>
              If public, your website can appear on MyVitae AI's public showcase when published.
            </div>
          </div>
          <Toggle
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            disabled={saving}
            aria-label="Public website"
            className="shrink-0"
          />
        </div>

        {/* Information Section */}
        <div className={`${typography.bodySmall} ${textColors.secondary} space-y-1`}>
          <div>
            <span className={`font-medium ${textColors.primary}`}>Published:</span>{' '}
            {publishedVersion ? 'Yes' : 'No'}
          </div>
          <div>
            <span className={`font-medium ${textColors.primary}`}>Published version:</span>{' '}
            {publishedVersion ? (publishedVersion.version_name || 'Untitled') : '-'}
          </div>
          {hasValidLastUpdate && (
            <div>
              <span className={`font-medium ${textColors.primary}`}>Last update:</span>{' '}
              {getDisplayDate({ last_edited_at: lastUpdated }, timezone)}
            </div>
          )}
        </div>

        {/* Danger Zone */}
        <div className={`pt-4 mt-2 border-t ${borders.default}`}>
          <div className={`${typography.label} text-red-700 dark:text-red-300`}>
            Danger zone
          </div>
          <div className={`${typography.caption} ${textColors.secondary} mt-1`}>
            Deleting this website will remove the entire website container and all its versions. This action cannot be undone.
          </div>
          <div className="mt-3">
            <DangerButton
              variant="outline"
              disabled={saving || !container?.id}
              onClick={() => {
                if (onRequestDelete && container?.id) {
                  onRequestDelete(container.id);
                }
              }}
            >
              Delete website
            </DangerButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

WebsiteSettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  container: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    domain: PropTypes.string,
    public: PropTypes.bool,
    latest_last_edited_at: PropTypes.string,
  }),
  versions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    version_name: PropTypes.string,
    is_published: PropTypes.bool,
    last_edited_at: PropTypes.string,
    updated_at: PropTypes.string,
    created_at: PropTypes.string,
  })),
  onSave: PropTypes.func.isRequired,
  onRequestDelete: PropTypes.func,
  username: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
};

export default WebsiteSettingsModal;

